import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
    DialogTrigger,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { useAuthInfo } from "@propelauth/react";
import { fetchWithToken, postWithToken } from "../../../api/apiBase";
import { X } from "lucide-react";


type MessageBiddersDialogProps = {
    // for pages without a scope in the url (eg the bid tab)
    scope_id?: string
    // pass open to control it from outside (eg a menu) - then there is no button
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

export default function MessageBiddersDialog({scope_id: scopeIdProp, open, onOpenChange: onOpenChangeProp}: MessageBiddersDialogProps) {
    const { accessToken } = useAuthInfo();
    const { project_id, scope_id: scopeIdParam } = useParams<string>();
    const scope_id = scopeIdProp ?? scopeIdParam;
    const [selfOpen, setSelfOpen] = useState<boolean>(false);
    const isOpen = open ?? selfOpen;

    const [recipients, setRecipients] = useState<string[]>([]);
    const [excluded, setExcluded] = useState<Set<string>>(new Set());
    const [showRecipients, setShowRecipients] = useState<boolean>(true);
    const [subject, setSubject] = useState<string>("");
    const [message, setMessage] = useState<string>("");
    const [sending, setSending] = useState<boolean>(false);
    const [status, setStatus] = useState<string>("");

    const fetchRecipients = async () => {
        if (!accessToken || !project_id || !scope_id) return;
        try {
            const perms = await fetchWithToken(
                `/projects/${project_id}/scope/${scope_id}/directory`,
                accessToken
            );
            const emails = perms
                .map((p: any) => p.user_id)
                .filter((e: string | null) => e != null);
            setRecipients(Array.from(new Set(emails)) as string[]);
        } catch (err) {
            console.error("Failed to load recipients", err);
        }
    };

    const activeRecipients = recipients.filter(e => !excluded.has(e));

    const toggleExclude = (email: string) => {
        setExcluded(prev => {
            const next = new Set(prev);
            if (next.has(email)) {
                next.delete(email);
            } else {
                next.add(email);
            }
            return next;
        });
    };

    const handleSend = async () => {
        if (!accessToken || !message.trim() || activeRecipients.length === 0) return;
        setSending(true);
        setStatus("");
        try {
            const res = await postWithToken(
                `/projects/${project_id}/message_bidders`,
                JSON.stringify({
                    subject: subject || null,
                    message,
                    scope_id: scope_id || null,
                    emails: activeRecipients,
                }),
                accessToken
            );
            setStatus(`Sent to ${res.sent} bidder(s)`);
            setSubject("");
            setMessage("");
        } catch (err) {
            setStatus("Failed to send");
        } finally {
            setSending(false);
        }
    };

    // on open, not in onOpenChange - that does not fire when the parent opens it
    useEffect(() => {
        if (isOpen) {
            fetchRecipients();
            setStatus("");
            setExcluded(new Set());
        }
    }, [isOpen]);

    const onOpenChange = (o: boolean) => {
        setSelfOpen(o);
        onOpenChangeProp?.(o);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            {open === undefined &&
                <DialogTrigger asChild>
                    <Button variant="outline">Email Bidders</Button>
                </DialogTrigger>
            }
            <DialogContent className="sm:max-w-2xl">
                <DialogTitle className="font-normal !text-[17px] mb-2">
                    Message All Bidders
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                    {recipients.length > 0
                        ? <span
                            className="cursor-pointer hover:underline"
                            onClick={() => setShowRecipients(!showRecipients)}
                          >
                            {activeRecipients.length} of {recipients.length} recipient(s) {showRecipients ? '▾' : '▸'}
                          </span>
                        : "No bidders invited to this scope yet."}
                </DialogDescription>

                {showRecipients && recipients.length > 0 && (
                    <div className="max-h-40 overflow-y-auto text-xs text-gray-600 bg-gray-50 rounded p-2 columns-2 gap-4">
                        {recipients.map((email) => (
                            <div key={email} className={`break-inside-avoid py-0.5 flex items-center justify-between ${excluded.has(email) ? 'line-through text-gray-400' : ''}`}>
                                <span>{email}</span>
                                <button
                                    type="button"
                                    onClick={() => toggleExclude(email)}
                                    className="ml-1 hover:text-red-500"
                                    title={excluded.has(email) ? "Re-include" : "Exclude"}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <Input
                    placeholder="Subject (optional)"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                />

                <Textarea
                    placeholder="Type your message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                />

                <Button
                    onClick={handleSend}
                    disabled={sending || !message.trim() || activeRecipients.length === 0}
                >
                    {sending ? "Sending..." : "Send Message"}
                </Button>

                <DialogFooter className="sm:justify-start">
                    {status && <p className="text-sm">{status}</p>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
